import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getSession, signOut, setMyVibe } from '../services/auth'
import {
  listenToFriends, listenToKnocks, listenToRange, listenToArchive,
  findUserByUsername, sendKnock, dismissKnock, removeFromArchive,
  renameFriend, setRange, releaseFriend,
  Friend, Knock, RangeStatus, ArchivedFriend,
} from '../services/friends'
import { track } from '../services/analytics'
import { listenToFeed, listenToSent, sendTingl, markOpened, createShareLink, Tingl, SentTingl } from '../services/tingls'
import { VIBES, resolveVibe, Flavor } from '../vibes'
import { authReady } from '../services/firebase'
import { useFCM, IncomingTingl } from '../hooks/useFCM'
import SendPanel from '../components/SendPanel'
import Detonation, { DetonationData } from '../components/Detonation'
import ShareModal from '../components/ShareModal'

const STATUS_OPTIONS = [
  { emoji: '🌊', label: 'Flowing' },
  { emoji: '🔥', label: 'On fire' },
  { emoji: '🌙', label: 'Low-key' },
  { emoji: '⚡', label: 'Wired' },
  { emoji: '🫂', label: 'Need a hug' },
  { emoji: '💭', label: 'In my head' },
  { emoji: '✨', label: 'Magic hours' },
]

function Orb({ color, size = 20 }: { color: string; size?: number }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: `radial-gradient(circle at 35% 32%, #ffffffcc 0%, ${color} 38%, ${color}22 78%, transparent 100%)`,
      boxShadow: `0 0 ${size * 0.7}px ${color}66`,
      flexShrink: 0,
    }} />
  )
}

export default function Home() {
  const nav = useNavigate()
  const [params] = useSearchParams()
  const session = getSession()
  const [friends, setFriends] = useState<Friend[]>([])
  const [knocks, setKnocks] = useState<Knock[]>([])
  const [rangeMap, setRangeMap] = useState<Record<string, RangeStatus>>({})
  const [feed, setFeed] = useState<Tingl[]>([])
  const [sendTo, setSendTo] = useState<Friend | null>(null)
  const [detonation, setDetonation] = useState<DetonationData | null>(null)
  const [replyTo, setReplyTo] = useState<Friend | null>(null)
  const [menuFor, setMenuFor] = useState<Friend | null>(null)
  const [myStatus, setMyStatus] = useState('🌊')
  const [addOpen, setAddOpen] = useState(false)
  const [addName, setAddName] = useState('')
  const [addMsg, setAddMsg] = useState('')
  const [listenErr, setListenErr] = useState('')
  const [archive, setArchive] = useState<ArchivedFriend[]>([])
  const [archiveOpen, setArchiveOpen] = useState(false)
  const [sent, setSent] = useState<SentTingl[]>([])
  const [sentOpen, setSentOpen] = useState(false)
  const [shareMe, setShareMe] = useState(false)
  const [outsideOpen, setOutsideOpen] = useState(false)
  const [outsideShare, setOutsideShare] = useState<{ url: string; phrase: string } | null>(null)


  useEffect(() => { if (!session) nav('/') }, [session, nav])

  // arrived via someone's invite link while logged in → knock automatically
  useEffect(() => {
    const kt = params.get('knock')
    if (!kt || !session) return
    findUserByUsername(kt).then((found) => {
      if (found && found.uid !== session.uid) {
        sendKnock(found.uid).then((r) => {
          if (r === 'knocked') setAddMsg(`✦ knocked on ${found.username}`)
        }).catch(() => {})
      }
      nav('/home', { replace: true })
    }).catch(() => {})
  }, [])

  useEffect(() => {
    let u1 = () => {}, u2 = () => {}, u3 = () => {}, u4 = () => {}, u5 = () => {}, u6 = () => {}
    authReady.then(() => {
      u1 = listenToFriends(setFriends, setListenErr)
      u2 = listenToFeed(setFeed, setListenErr)
      u3 = listenToKnocks(setKnocks, setListenErr)
      u4 = listenToRange(setRangeMap)
      u5 = listenToArchive(setArchive)
      u6 = listenToSent(setSent)
    })
    return () => { u1(); u2(); u3(); u4(); u5(); u6() }
  }, [])

  // FCM foreground — tingls AND knocks detonate live
  const onIncoming = useCallback((t: IncomingTingl) => {
    setDetonation({
      vibeKey: t.vibeKey, vibeEmoji: t.vibeEmoji, vibeLabel: t.vibeLabel,
      vibeColor: t.vibeColor, phrase: t.phrase, fromName: t.fromName,
      direction: 'received',
    })
    if (t.vibeKey === 'KNOCK' && t.fromUid) {
      // answering the knock = accepting — pseudo-friend for the reply
      setReplyTo({
        uid: t.fromUid, username: t.fromName, label: t.fromName,
        friendshipId: [getSession()?.uid || '', t.fromUid].sort().join('_'),
        streak: 0, streakTier: 'none',
      })
    }
    if (t.tinglId) markOpened(t.tinglId)
  }, [])
  useFCM(onIncoming)

  const pickStatus = async (emoji: string, label: string) => {
    setMyStatus(emoji)
    if ('vibrate' in navigator) navigator.vibrate(15)
    await setMyVibe(emoji, label)
  }

  const fireTingl = async (friend: Friend, vibeKey: string, flavor: Flavor) => {
    setSendTo(null)
    const v = VIBES[vibeKey]
    try {
      const { phrase } = await sendTingl(
        friend.uid, v.key, v.glyph, v.label, v.color, friend.friendshipId, flavor.phrase, flavor.intensity
      )
      track('tingl_sent', { vibe: v.key, flavor: flavor.key })
      setDetonation({
        vibeKey: v.key, vibeEmoji: v.glyph, vibeLabel: v.label,
        vibeColor: v.color, phrase, fromName: friend.label, direction: 'sent',
        intensity: flavor.intensity,
      })
    } catch {
      alert('Could not send — check your connection')
    }
  }

  const fireOutside = async (vibeKey: string, flavor: Flavor) => {
    setOutsideOpen(false)
    const v = VIBES[vibeKey]
    try {
      const { url } = await createShareLink(v.key, v.glyph, v.color, flavor.phrase, flavor.intensity)
      track('tingl_shared_outside', { vibe: v.key, flavor: flavor.key })
      setOutsideShare({ url, phrase: flavor.phrase })
    } catch {
      alert('Could not create the link — try again')
    }
  }

  const answerKnock = (k: Knock) => {
    // answering = accepting. the friendship is born from the vibe you send.
    setSendTo({
      uid: k.from, username: k.fromName, label: k.fromName,
      friendshipId: [session?.uid || '', k.from].sort().join('_'),
      streak: 0, streakTier: 'none',
    })
  }

  const openFeedItem = (t: Tingl) => {
    setDetonation({
      vibeKey: t.vibeKey, vibeEmoji: t.vibeEmoji, vibeLabel: t.vibeLabel,
      vibeColor: t.vibeColor, phrase: t.phrase, fromName: t.fromName,
      direction: 'received', intensity: t.intensity || 1.0,
    })
    const sender = friends.find((f) => f.uid === t.from)
    setReplyTo(sender || null)
    if (t.id) markOpened(t.id)
  }

  const doKnock = async () => {
    setAddMsg('')
    try {
      const found = await findUserByUsername(addName)
      if (!found) { setAddMsg('No one by that name in the protocol'); return }
      if (found.uid === session?.uid) { setAddMsg("That's you 🙃"); return }
      const result = await sendKnock(found.uid)
      if (result === 'already-friends') setAddMsg(`${found.username} is already in your squad`)
      else if (result === 'already-knocked') setAddMsg(`You already knocked — give it time`)
      else {
        track('knock_sent')
        setAddMsg(`✦ knock sent — if ${found.username} answers, they'll appear in your squad`)
        setAddName('')
        setTimeout(() => { setAddOpen(false); setAddMsg('') }, 2600)
      }
    } catch (e: any) {
      const code = e?.code || ''
      setAddMsg(String(code).includes('permission') ? 'Session expired — log out and back in'
        : `Could not knock: ${code || e?.message || 'unknown'}`)
    }
  }

  const doRename = async (f: Friend) => {
    setMenuFor(null)
    const label = prompt(`Rename ${f.label}? (only you see this)`, f.label)
    if (label && label.trim()) await renameFriend(f.uid, label.trim())
  }

  const doRange = async (f: Friend, status: RangeStatus | null) => {
    setMenuFor(null)
    await setRange(f.uid, status)
  }

  const doRelease = async (f: Friend) => {
    setMenuFor(null)
    if (!window.confirm(`Release ${f.label} from your squad? They'll simply be gone — no notification. You can knock again anytime within 90 days.`)) return
    await releaseFriend(f)
  }

  const timeAgo = (t: any) => {
    if (!t?.toDate) return ''
    const s = Math.floor((Date.now() - t.toDate().getTime()) / 1000)
    if (s < 60) return 'just now'
    if (s < 3600) return `${Math.floor(s / 60)}m ago`
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`
    return `${Math.floor(s / 86400)}d ago`
  }

  return (
    <div className="screen" style={{ paddingBottom: 40 }}>
      <div className="orb" style={{ width: 240, height: 240, background: 'var(--mint)', top: -100, right: -80 }} />

      <header style={{
        position: 'sticky', top: 0, zIndex: 20,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginTop: 'calc(env(safe-area-inset-top, 0px) * -1)',
        padding: 'calc(16px + env(safe-area-inset-top, 0px)) 20px 12px',
        background: 'rgba(8,8,8,0.85)', backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
      }}>
        <div className="logo" style={{ fontSize: 27 }}>
          <span className="t">t</span><span className="ingl">ingl</span>
          <span className="logo-dot" style={{ width: 6, height: 6, marginBottom: 19, marginLeft: -14 }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, flexShrink: 1 }}>
          <button onClick={() => setShareMe(true)} style={{
            background: 'rgba(0,255,209,0.1)', border: '1px solid rgba(0,255,209,0.3)',
            borderRadius: 100, color: 'var(--mint)', fontSize: 10,
            padding: '5px 9px', cursor: 'pointer', fontFamily: 'Syne', fontWeight: 800,
            whiteSpace: 'nowrap', flexShrink: 0,
          }}>⇗ share</button>
          <span className="eyebrow" style={{ fontSize: 9, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 92 }}>{session?.username}</span>
          <button onClick={signOut} style={{
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 100, color: 'rgba(255,255,255,0.5)', fontSize: 11,
            padding: '6px 12px', cursor: 'pointer', fontFamily: 'Space Grotesk',
          }}>exit</button>
        </div>
      </header>

      <main style={{ padding: '18px 16px 0', maxWidth: 440, width: '100%', margin: '0 auto', position: 'relative', zIndex: 2 }}>

        {listenErr && (
          <div className="card" style={{ padding: '12px 16px', marginBottom: 16, border: '1px solid rgba(255,45,120,0.4)', fontSize: 12, color: 'var(--pink)' }}>
            sync error: {listenErr}
          </div>
        )}

        {/* KNOCKS AT YOUR DOOR */}
        {knocks.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div className="eyebrow" style={{ marginBottom: 10 }}>knocking at your door</div>
            {knocks.map((k) => (
              <div key={k.id} className="card fade-in" style={{
                padding: '16px 18px', marginBottom: 10,
                border: '1px solid rgba(0,255,209,0.35)',
                boxShadow: '0 0 24px rgba(0,255,209,0.12)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <Orb color="#00FFD1" size={26} />
                  <div>
                    <div className="syne" style={{ fontWeight: 700, fontSize: 16, letterSpacing: '-0.02em' }}>
                      {k.fromName}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-faint)' }}>
                      wants into your orbit · answer with a vibe or let it fade
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="pill grad" style={{ padding: '12px', fontSize: 12 }} onClick={() => answerKnock(k)}>
                    Answer ↩
                  </button>
                  <button className="pill ghost" style={{ padding: '12px', fontSize: 12 }} onClick={() => dismissKnock(k.id)}>
                    Let it fade
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* MY VIBE */}
        <div className="eyebrow" style={{ marginBottom: 10 }}>my vibe right now</div>
        <div style={{ display: 'flex', gap: 9, overflowX: 'auto', paddingBottom: 6, marginBottom: 24, scrollbarWidth: 'none' }}>
          {STATUS_OPTIONS.map((s) => (
            <button key={s.emoji} onClick={() => pickStatus(s.emoji, s.label)} style={{
              flexShrink: 0, padding: '9px 15px', borderRadius: 100,
              background: 'var(--glass)', cursor: 'pointer',
              border: myStatus === s.emoji ? '1px solid var(--mint)' : '1px solid var(--glass-border)',
              boxShadow: myStatus === s.emoji ? '0 0 18px rgba(0,255,209,0.18)' : 'none',
              display: 'flex', alignItems: 'center', gap: 6,
              fontFamily: 'Space Grotesk', color: 'rgba(255,255,255,0.7)',
            }}>
              <span style={{ fontSize: 16 }}>{s.emoji}</span>
              <span style={{ fontSize: 11, fontWeight: 600 }}>{s.label}</span>
            </button>
          ))}
        </div>

        {/* SQUAD */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div className="eyebrow">your squad</div>
          <div style={{ display: 'flex', gap: 16 }}>
            <button onClick={() => setOutsideOpen(true)} style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)',
              fontFamily: 'Syne', fontWeight: 800, fontSize: 12,
              letterSpacing: '0.05em', cursor: 'pointer', textTransform: 'uppercase',
            }}>⇗ beyond</button>
            <button onClick={() => setAddOpen(!addOpen)} style={{
              background: 'none', border: 'none', color: 'var(--mint)',
              fontFamily: 'Syne', fontWeight: 800, fontSize: 12,
              letterSpacing: '0.05em', cursor: 'pointer', textTransform: 'uppercase',
            }}>✦ knock</button>
          </div>
        </div>

        {addOpen && (
          <div className="card fade-in" style={{ padding: 16, marginBottom: 12 }}>
            <input
              className="field" placeholder="Their username (e.g. MidnightOtter)"
              value={addName} onChange={(e) => setAddName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && doKnock()}
              autoCapitalize="none" style={{ marginBottom: 10 }}
            />
            <button className="pill grad" style={{ padding: '13px' }} onClick={doKnock} disabled={!addName.trim()}>
              Knock on their door
            </button>
            {addMsg && <div style={{ fontSize: 13, color: addMsg.includes('✦') ? 'var(--mint)' : 'var(--pink)', marginTop: 10, textAlign: 'center', lineHeight: 1.5 }}>{addMsg}</div>}
          </div>
        )}

        <div className="card" style={{ marginBottom: 26, overflow: 'hidden' }}>
          {friends.length === 0 && (
            <div style={{ padding: '32px 22px', textAlign: 'center' }}>
              <div style={{ marginBottom: 14, display: 'flex', justifyContent: 'center' }}><Orb color="#00FFD1" size={30} /></div>
              <div className="syne" style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 8 }}>
                the void is silent
              </div>
              <div style={{ fontSize: 13.5, color: 'var(--text-dim)', lineHeight: 1.65, marginBottom: 18 }}>
                tingl begins with a knock. Tap <span style={{ color: 'var(--mint)', fontWeight: 700 }}>✦ knock</span> above, enter a friend's username, and they'll appear here once they answer with a vibe.
              </div>
              <button onClick={() => setAddOpen(true)} className="pill grad" style={{ maxWidth: 220, margin: '0 auto', padding: '13px' }}>
                ✦ knock on someone
              </button>
              <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 14, lineHeight: 1.5 }}>
                don't know anyone's username yet?<br />share yours: <span style={{ color: '#fff', fontWeight: 600 }}>{session?.username}</span>
              </div>
              <button onClick={() => setOutsideOpen(true)} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--mint)', fontFamily: 'Space Grotesk', fontSize: 12.5,
                fontWeight: 600, marginTop: 12, textDecoration: 'underline',
              }}>
                or send a tingl to anyone — even off tingl →
              </button>
            </div>
          )}
          {friends.map((f, i) => {
            const range = rangeMap[f.uid]
            return (
              <div key={f.uid}>
                {i > 0 && <div style={{ height: 1, background: 'var(--glass-border)', margin: '0 18px' }} />}
                <div
                  onClick={() => setSendTo(f)}
                  style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '15px 18px', cursor: 'pointer', opacity: range === 'out' ? 0.45 : 1 }}
                >
                  <div style={{
                    width: 48, height: 48, borderRadius: 15, flexShrink: 0,
                    background: 'rgba(0,255,209,0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
                  }}>
                    {f.currentVibe || <Orb color="#00FFD1" size={18} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="syne" style={{ fontWeight: 700, fontSize: 16, letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: 7 }}>
                      {f.label}
                      {f.streak > 0 && (
                        <span style={{ fontSize: 10, color: 'var(--mint)', fontFamily: 'Space Grotesk', fontWeight: 600 }}>
                          {f.streakTier === 'soul-link' ? '🔗' : f.streakTier === 'crystal' ? '💎' : '⚡'} {f.streak}
                        </span>
                      )}
                      {range === 'muted' && <span style={{ fontSize: 9, color: 'var(--text-faint)', fontFamily: 'Space Grotesk' }}>◦ muted</span>}
                      {range === 'out' && <span style={{ fontSize: 9, color: 'var(--text-faint)', fontFamily: 'Space Grotesk' }}>⌀ out of range</span>}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 1 }}>
                      {f.currentVibeLabel ? f.currentVibeLabel.toLowerCase() + ' rn' : '@' + f.username}
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setMenuFor(f) }}
                    style={{ background: 'none', border: 'none', color: 'var(--text-faint)', fontSize: 17, cursor: 'pointer', padding: 6, letterSpacing: 2 }}
                  >⋯</button>
                  <span className="syne" style={{ fontSize: 11, color: 'var(--mint)', fontWeight: 800, letterSpacing: '0.04em' }}>
                    TINGL →
                  </span>
                </div>
              </div>
            )
          })}
        </div>

        {/* RELEASED — 90 days to re-knock */}
        {archive.length > 0 && (
          <div style={{ marginBottom: 26 }}>
            <button onClick={() => setArchiveOpen(!archiveOpen)} style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span className="eyebrow">released · {archive.length}</span>
              <span style={{ color: 'var(--text-faint)', fontSize: 10 }}>{archiveOpen ? '▴' : '▾'}</span>
            </button>
            {archiveOpen && (
              <div className="card fade-in" style={{ marginTop: 10, overflow: 'hidden' }}>
                {archive.map((a, i) => (
                  <div key={a.uid}>
                    {i > 0 && <div style={{ height: 1, background: 'var(--glass-border)', margin: '0 18px' }} />}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 18px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>{a.label || a.username}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>@{a.username}</div>
                      </div>
                      <button
                        onClick={async () => {
                          const r = await sendKnock(a.uid)
                          if (r === 'knocked' || r === 'already-friends') await removeFromArchive(a.uid)
                        }}
                        style={{
                          background: 'rgba(0,255,209,0.1)', border: '1px solid rgba(0,255,209,0.3)',
                          borderRadius: 100, color: 'var(--mint)', fontFamily: 'Syne',
                          fontWeight: 800, fontSize: 11, padding: '8px 16px', cursor: 'pointer',
                          textTransform: 'uppercase', letterSpacing: '0.04em',
                        }}
                      >✦ knock again</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* FEED */}
        <div className="eyebrow" style={{ marginBottom: 10 }}>what just landed</div>
        {feed.length === 0 && (
          <div style={{ fontSize: 13, color: 'var(--text-faint)', padding: '8px 4px 30px' }}>
            Nothing yet — tingls you receive appear here.
          </div>
        )}
        {sent.length > 0 && (
          <div style={{ marginTop: 26, marginBottom: 20 }}>
            <button onClick={() => setSentOpen(!sentOpen)} style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
            }}>
              <span className="eyebrow">my transmissions · {sent.length}</span>
              <span style={{ color: 'var(--text-faint)', fontSize: 10 }}>{sentOpen ? '▴' : '▾'}</span>
            </button>
            {sentOpen && (
              <div className="card fade-in" style={{ overflow: 'hidden' }}>
                {sent.map((t, i) => (
                  <div key={t.id}>
                    {i > 0 && <div style={{ height: 1, background: 'var(--glass-border)', margin: '0 18px' }} />}
                    <div
                      onClick={() => setDetonation({
                        vibeKey: t.vibeKey, vibeEmoji: t.vibeEmoji, vibeLabel: t.vibeLabel,
                        vibeColor: t.vibeColor, phrase: t.phrase, fromName: t.toName,
                        direction: 'sent', intensity: t.intensity || 1.0,
                      })}
                      style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '13px 16px', cursor: 'pointer' }}
                    >
                      <div style={{
                        width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                        background: `${t.vibeColor}18`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <div style={{
                          width: 18, height: 18, borderRadius: '50%',
                          background: `radial-gradient(circle at 35% 32%, #ffffffcc 0%, ${t.vibeColor} 38%, ${t.vibeColor}22 78%, transparent 100%)`,
                          boxShadow: `0 0 12px ${t.vibeColor}66`,
                        }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 600 }}>
                          you → <span style={{ color: 'rgba(255,255,255,0.7)' }}>{t.toName}</span>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 1, fontStyle: 'italic' }}>
                          <span style={{ color: t.vibeColor, fontWeight: 700, fontStyle: 'normal' }}>{resolveVibe(t.vibeKey).label.toLowerCase()}</span> · "{t.phrase}"
                        </div>
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-faint)', flexShrink: 0 }}>
                        {timeAgo(t.sentAt)}
                      </div>
                    </div>
                  </div>
                ))}
                <div style={{ padding: '10px 18px 12px', borderTop: '1px solid var(--glass-border)', fontSize: 10, color: 'var(--text-faint)', textAlign: 'center', letterSpacing: '0.05em' }}>
                  private · no receipts · tap to replay
                </div>
              </div>
            )}
          </div>
        )}

        {feed.filter((t) => {
          const ts = (t.sentAt as any)?.toDate?.()
          return !ts || (Date.now() - ts.getTime()) < 7 * 86400000
        }).map((t) => (
          <div
            key={t.id}
            onClick={() => openFeedItem(t)}
            className="card fade-in"
            style={{
              display: 'flex', alignItems: 'center', gap: 13,
              padding: '13px 16px', marginBottom: 9, cursor: 'pointer',
              border: t.opened ? '1px solid var(--glass-border)' : `1px solid ${t.vibeColor}55`,
              boxShadow: t.opened ? 'none' : `0 0 22px ${t.vibeColor}22`,
            }}
          >
            <div style={{
              width: 44, height: 44, borderRadius: 14, flexShrink: 0,
              background: `${t.vibeColor}18`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Orb color={t.vibeColor} size={20} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{t.fromName} → you</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 1 }}>
                sent a <span style={{ color: t.vibeColor, fontWeight: 700 }}>{resolveVibe(t.vibeKey).label.toLowerCase()}</span> tingl
              </div>
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-faint)', flexShrink: 0 }}>
              {t.opened ? timeAgo(t.sentAt) : '● new'}
            </div>
          </div>
        ))}
      </main>

      {/* FRIEND MENU — the signal controls */}
      {menuFor && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setMenuFor(null) }}
          style={{
            position: 'fixed', inset: 0, zIndex: 600,
            background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 14,
          }}
        >
          <div className="fade-in" style={{
            width: '100%', maxWidth: 420, background: '#101010',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '32px 32px 24px 24px',
            padding: '26px 18px calc(26px + env(safe-area-inset-bottom))',
          }}>
            <div className="eyebrow" style={{ marginBottom: 4 }}>signal controls</div>
            <div className="syne" style={{ fontWeight: 800, fontSize: 20, letterSpacing: '-0.03em', marginBottom: 18 }}>
              {menuFor.label}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button className="pill ghost" style={{ textAlign: 'left', textTransform: 'none', fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14 }} onClick={() => doRename(menuFor)}>
                ✎ &nbsp;Rename (only you see it)
              </button>
              {rangeMap[menuFor.uid] === 'muted' ? (
                <button className="pill ghost" style={{ textAlign: 'left', textTransform: 'none', fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14 }} onClick={() => doRange(menuFor, null)}>
                  ◦ &nbsp;Unmute their frequency
                </button>
              ) : (
                <button className="pill ghost" style={{ textAlign: 'left', textTransform: 'none', fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14 }} onClick={() => doRange(menuFor, 'muted')}>
                  ◦ &nbsp;Mute their frequency — they send, you don't feel it
                </button>
              )}
              {rangeMap[menuFor.uid] === 'out' ? (
                <button className="pill ghost" style={{ textAlign: 'left', textTransform: 'none', fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14 }} onClick={() => doRange(menuFor, null)}>
                  ⌀ &nbsp;Bring back in range
                </button>
              ) : (
                <button className="pill ghost" style={{ textAlign: 'left', textTransform: 'none', fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14 }} onClick={() => doRange(menuFor, 'out')}>
                  ⌀ &nbsp;Out of range — their tingls dissolve, they never know
                </button>
              )}
              <button className="pill ghost" style={{ textAlign: 'left', textTransform: 'none', fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14, color: 'var(--pink)' }} onClick={() => doRelease(menuFor)}>
                ↯ &nbsp;Release from squad — 90 days to re-knock
              </button>
            </div>
          </div>
        </div>
      )}

      {sendTo && (
        <SendPanel
          friend={sendTo}
          onClose={() => setSendTo(null)}
          onPick={(vibeKey, flavor) => fireTingl(sendTo, vibeKey, flavor)}
        />
      )}

      {outsideOpen && (
        <SendPanel
          friend={{
            uid: '', username: '', label: 'someone out there',
            friendshipId: '', streak: 0, streakTier: 'none',
          }}
          onClose={() => setOutsideOpen(false)}
          onPick={(vibeKey, flavor) => fireOutside(vibeKey, flavor)}
        />
      )}

      {shareMe && session && (
        <ShareModal
          title={`knock on ${session.username}`}
          subtitle="your personal doorway"
          url={`https://tingl.me/u/${session.username}`}
          shareText={`knock on me — i'm ${session.username} on tingl. it sends feelings in 1 second:`}
          onClose={() => setShareMe(false)}
        />
      )}

      {outsideShare && (
        <ShareModal
          title="your tingl is ready"
          subtitle="send it to anyone"
          url={outsideShare.url}
          shareText={`i sent you a feeling — "${outsideShare.phrase}" — open it:`}
          onClose={() => setOutsideShare(null)}
        />
      )}

      {detonation && (
        <Detonation
          data={detonation}
          onDone={() => { setDetonation(null); setReplyTo(null) }}
          onSendBack={
            detonation.direction === 'received' && replyTo
              ? () => { const f = replyTo; setDetonation(null); setReplyTo(null); setSendTo(f) }
              : undefined
          }
        />
      )}
    </div>
  )
}
